/******************************************************************************
File: cgp-grid.component.ts
Author: Jared Craig - jared.craig@gm.com
Date: 10-10-2017
Description: Generic, reusable interface for kendo grid.
Other contributors: Neel Radia
******************************************************************************/


import {takeUntil} from 'rxjs/operators';
import { Component, OnInit, Input, ViewChild, OnDestroy, HostListener, ElementRef, AfterViewInit, } from '@angular/core';
import { process, FilterDescriptor } from '@progress/kendo-data-query';
import { GridDataResult, FilterService, DataStateChangeEvent, GridComponent } from '@progress/kendo-angular-grid';
import { Observable, BehaviorSubject ,  Subject ,  of } from 'rxjs';

// CGP Dependencies
import { CgpConfirmationDialogService } from '../cgp-confirmation-dialog/cgp-confirmation-dialog.service';
import { CgpSnackbarService } from '../cgp-snackbar/cgp-snackbar.service';
import { CgpAlertDialogService } from '../cgp-alert-dialog/cgp-alert-dialog.service';
import { DataGridTemplate } from './data-grid.template';
import { messages, exceptions, exceptionTypes } from './data-grid.config';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { ColumnTemplatePipe } from './data-grid.pipe';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfigurationService } from './configuration/configuration.service';

const MIN_COLUMN_WIDTH = 160;
const OFFSET_HEIGHT = 160;
const ROW_HEIGHT = 48;
const GROUP_ROW_HEIGHT = 60;

@Component({
    selector: 'data-grid',
    templateUrl: './data-grid.component.html',
    styleUrls: ['./data-grid.component.scss'],
})
export class DataGridComponent implements OnInit, OnDestroy, AfterViewInit {


    /**
     * 
     * @param cgpConfirmationDialogService 
     * @param cgpSnackbarService 
     * @param cgpAlertDialogService 
     */
    constructor(
        private readonly configurationService: ConfigurationService,
        private readonly cgpConfirmationDialogService: CgpConfirmationDialogService,
        private readonly cgpSnackbarService: CgpSnackbarService,
        private readonly cgpAlertDialogService: CgpAlertDialogService,
        private readonly formBuilder: FormBuilder,
        private readonly router: Router,
        private readonly elRef: ElementRef
    ) {
    }
    /**
     * 
     */
    @ViewChild('grid', { static: true }) grid: GridComponent;
    /**
     * 
     */
    filterForm = this.formBuilder.group({ filter: [] });
    /**
     * Configuration dialog visibility
     */
    isShowConfig: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    /**
     * 
     */
    protected gridData: Observable<GridDataResult>;
    /**
     * 
     */
    private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
    /**
     * 
     */
    @Input() template: DataGridTemplate;
    /**
     * 
     */
    @Input() parentEditDataItem: any;
    /**
     * 
     */
    private exportDate: string;
    /**
     * 
     */
    private gridHeight: number = OFFSET_HEIGHT;
    /**
    * 
    */
    private gridSize: number;
    /**
     * 
     */
    private showcaseBody: any;
    /**
     * 
     */
    ngAfterViewInit(): void {
        if (this.template.saveUserSettings && !this.parentEditDataItem) {
            this.showcaseBody = this.elRef.nativeElement;
            let el = this.showcaseBody;
            let counter = 0;
            do {
                try {
                    counter = counter + 1;
                    el = el.offsetParent;

                    if (el.children.length && el.children[1].className === "showcase-body") {
                        this.showcaseBody = el.children[1];
                        return;

                    }
                } catch (e) {
                    console.error(e);
                    break;
                }
            } while (counter < 10);
        }
    }

    /**
     * 
     */
    ngOnInit() {
        if (this.template) {
            this.template.httpResponse.pipe(
                takeUntil(this.ngUnsubscribe))
                .subscribe(x => {
                    if (x) this.handleResponse(x.response, x.message);
                })
        }
        this.template.changed.pipe(
            takeUntil(this.ngUnsubscribe))
            .subscribe(x => this.refresh(true));

        this.template.gridDataChanged.pipe(
            takeUntil(this.ngUnsubscribe))
            .subscribe(x => this.setGridData(this.template.data));


        this.filterForm.get('filter').valueChanges.pipe(
            takeUntil(this.ngUnsubscribe))
            .subscribe(x => this.handleFilterChange);
    }


    /**
     * 
     */
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }


    /**
     * 
     * @param state 
     */
    onStateChange(state: DataStateChangeEvent): void {
        this.template.gridState = state;
        if (this.template.saveUserSettings) {
            this.configurationService.saveUserSettings(state);
        }

        const _dataResult = process(this.template.data, this.template.gridState);
        this.gridData = of(_dataResult);
        this.gridSize = this.template.data.length;
        this.getHeight(true, state);
        this.refresh(false);
    }


    /**
     * 
     * @param forceRefresh 
     */
    refresh(forceRefresh: boolean) {
        this.template.isLoading.next(true);
        const _options = {
            parentEditDataItem: this.parentEditDataItem,
            force: forceRefresh
        }
        this.template.read(_options)
            .then(response => {
                this.setGridData(response);
                this.template.isLoading.next(false);
                this.template.isSelectAll = false;
                this.template.selectedRows = [];

            })
            .catch(e => this.handleError(e, exceptions.gridRead))
    }


    /**
     * 
     */
    getColumnValue = (column: any, dataItem: any): string => {
        const _field: string = column.detail ? column.detail.field : column.field;
        const _obj: any = dataItem[_field];
        let response = _obj;

        if (!_obj) {
            response = dataItem;
            column.field
                .split('.')
                .forEach(f => response ? response = response[f] : null);
        }

        if (_obj && column.detail) {
            response = _obj[column.detail.textField];
        } else if (column.template) {
            response = new ColumnTemplatePipe()
                .transform(column, dataItem);
        }
        return response ? response : '';
    }


    getColumnValueTooltip = (column: any, dataItem: any): string => {
        let value = this.getColumnValue(column, dataItem);
        if (value.startsWith('http')) {
            value = value.split('.').pop();
        }
        return value;
    }

    /**
     * 
     */
    getFilterValue = (column, filter) => {
        try {
            return filter.filters[0].value;
        } catch (e) {
            return '';
        }
    }


    /**
     * 
     */
    handleFilterChange = (e: any, column: any, filterService: FilterService) => {
        filterService.filter({
            filters: this.getFilters(column.field, e),
            logic: 'or'
        });
    }


    /**
     * 
     * @param field 
     * @param value 
     */
    private getFilters(field, value): any[] {
        return this.getFilterChangeValue(value)
            .map(f => f = {
                field,
                operator: 'contains',
                value
            } as FilterDescriptor);
    }


    /**
     * 
     * @param dataItem 
     */
    private getFilterChangeValue(dataItem: any): any[] {
        if (dataItem) {
            return dataItem.data ? [dataItem.data] :
                typeof (dataItem) === typeof ([]) ? dataItem : [dataItem];
        }
        return [];
    }


    /**
     * Inline grid (cancel) event handler
     */
    cancelHandler = (obj: any) => {
        if (obj) this.closeEditor(obj.sender, obj.rowIndex)
        this.template.isEditing.next(false);
    }


    /**
     * Grid (add) event handler
     * Issue template's (onCreate) callback
     * If editing inline, open new row for editing to top of grid
     */
    addHandler = ({ sender, dataItem }) => {
        this.template.onCreate(dataItem);

        if (!this.template.modal) {
            this.closeEditor(sender);
            if (this.template.editForm) {
                sender.addRow(this.template.editForm);
            } else {
                this.handleError(exceptions.editForm, exceptionTypes.undefined)
            }
        }
    }


    /**
     * Grid (edit) event handler
     * Issue template's (onCreate) callback
     * If editing inline, open selected grid row for editing
     */
    editHandler = ({ sender, rowIndex, dataItem }) => {
        const _lastRow = this.template.editedRowIndex;
        this.template.onEdit(dataItem, rowIndex);

        if (!this.template.modal) {
            this.closeEditor(sender, _lastRow);
            sender.editRow(rowIndex, this.template.editForm);
            this.template.isEditing.next(true);
        }
    }


    /**
     * Grid (save) event handler
     * Issue template's (onSave) callback
     */
    saveHandler = ({ sender, rowIndex, formGroup, isNew }) => {
        this.template.onSave({ dataItem: formGroup.value, isNew });
        this.closeEditor(sender, rowIndex);
        this.template.isEditing.next(false);
    }


    /**
     * Grid (remove) event handler
     * Prompt user with confirmation dialog
     * YES: Issue template's (delete) callback and close dialog
     * NO: Close dialog
     */
    removeHandler = ({ dataItem }) => {
        const source: string = this.template.getSource(dataItem);
        if (this.template.deleteValidation) {
            this.template.deleteValidationService(dataItem)
                .then(response => {
                    const customMessage = response;
                    this.cgpConfirmationDialogService.showConfirmationDialog({
                        title: 'Delete Confirmation',
                        message: customMessage ? customMessage : `${messages.recordDeleteConfirm}${source}?`,
                        alert: 'danger',
                        onYesClick: () => {
                            this.onRemove(dataItem, source);
                        },
                        onNoClick: () => {
                            this.cgpConfirmationDialogService.closeConfirmationDialog();
                        }
                    });
                })
        }
        else {
            const customMessage = this.template.getDeleteMessage(dataItem);
            this.cgpConfirmationDialogService.showConfirmationDialog({
                title: 'Delete Confirmation',
                message: customMessage ? customMessage : `${messages.recordDeleteConfirm}${source}?`,
                alert: 'danger',
                onYesClick: () => {
                    this.onRemove(dataItem, source);
                },
                onNoClick: () => {
                    this.cgpConfirmationDialogService.closeConfirmationDialog();
                }
            });
        }
    }

    private onRemove(dataItem, source) {
        const response = this.template.delete(dataItem);
        this.handleResponse(response, source + messages.recordDeleteSuccess);
        this.cgpConfirmationDialogService.closeConfirmationDialog();
    }


    /**
     * 
     */
    handleToolBarButtonClick = (button) => {
        button.callback()
        if (button.refresh) this.refresh(true);
    }


    /**
     * 
     */
    handleCommandButtonClick = (button, dataItem) => {
        button.callback(button, dataItem);
        if (button.refresh) this.refresh(true);
    }


    /**
     * 
     */
    onSelectionChange = (e) => {
        const item = this.template.selectedRows.find(x => x.index === e.index),
            index = item ? this.template.selectedRows.indexOf(item) : -1;

        if (this.template.selectableSettings.mode === 'single') {
            this.template.selectedRows = e.selectedRows;
        } else {
            if (e.selected) {
                if (index < 0) this.template.selectedRows.push(e.selectedRows[0])
            } else {
                this.template.selectedRows.splice(index, 1);
            }
        }

        this.template.select(this.template.selectedRows);
    }


    /**
     * 
     */
    isRowSelected = (e) => {
        return this.template.selectedRows.some(x => x.index === e.index);
    }


    /**
     * 
     */
    isShowCommandColumn = (): boolean => {
        try {
            const ctrl = this.template.controls;
            return ctrl && (ctrl.edit || ctrl.delete || (ctrl.customButtons && ctrl.customButtons.length > 0));
        } catch (e) {
            return true;
        }
    }


    /**
     * 
     */
    isFilterTemplate = (template: any): boolean => {
        return template && (
            template.type === 'multiselect' ||
            template.type === 'combobox' ||
            template.type === 'form'
        );
    }

    // isGroupingField = (columnField: string) => {
    //     return this.template.gridState.group.length > 0 && columnField == this.template.gridState.group[0].field;            
    // }

    isGroupingField(columnField: string) {
        const test = this.template.gridState.group.length > 0;
        const test2 = columnField === this.template.gridState.group[0].field;
        return test && test2;
    }


    /**
     * 
     */
    toggleSelectAll = (e) => {
        this.template.isSelectAll = e.target.checked;
        this.template.selectedRows = this.template.isSelectAll ?
            this.template.data.map((item, i) => {
                return {
                    dataItem: item,
                    index: i + this.template.gridState.skip
                }
            }) : [];
        this.template.select(this.template.selectedRows);
    }


    /**
     * 
     */
    getColumnWidth = (column) => {
        return column.field && !column.width ? MIN_COLUMN_WIDTH : column.width === '*' ? null : column.width;
    }


    /**
     * 
     */
    getControlColumnWidth = () => {
        const width = 37;
        let baseWidth = 24;
        const ctrl = this.template.controls;
        if (ctrl.width) return ctrl.width;
        if (ctrl.edit) baseWidth += width;
        if (ctrl.delete) baseWidth += width;
        if (ctrl.customButtons) baseWidth += (ctrl.customButtons.length * width);
        if (!this.template.modal && !ctrl.delete) baseWidth += width;
        return baseWidth;
    }


    /**
     * 
     */
    getDetailRowHeader = (dataItem): string => {
        try {
            const dt = this.template.detailTemplate,
                header = dt.detailTitleField ?
                    (dataItem[dt.detailTitleField] + ': ') : '';
            return header + dt.title;
        } catch (e) {
            return "";
        }
    }


    /**
     * 
     */
    private getDatePath(): string {
        if (!this.exportDate) {
            const url: any[] = this.router.url.split('/');
            if (url.length > 0 && url[0] === '') url.shift();
            const date = new Date();
            this.exportDate = url.concat([date.getMonth() + 1, date.getDate(), date.getFullYear()])
                .join('_');
        }
        return this.exportDate;
    }


    /**
     * 
     */
    getExportFilename = (): string => {
        return this.getFileName('.xlsx');
    }


    private getFileName(extension: string): string {
        const _fileName = this.getDatePath() + extension;

        try {
            const fileName: string = this.template.fileName ?
                this.template.fileName : _fileName;
            return fileName.toLowerCase().endsWith(extension) ?
                fileName : fileName + extension;
        } catch (e) {
            return `grid.pdf${extension}`;
        }
    }


    /**
     * 
     */
    getPDFExportFilename = (): string => {
        return this.getFileName('.pdf');
    }

    /**
     * Fired when export to pdf button is clicked
     * 
     */
    onPDFExport = (e: any) => {
        this.refresh(false);
    }

    /**
     * 
     */
    isSelectAllDisabled = (): boolean => {
        return !this.template.data || this.template.data.length < 1;
    }


    /**
     * Show configuration dialog
     */
    showConfigDialog = () => this.isShowConfig.next(true);


    /**
     * 
     */
    isShowToolbar = (): boolean => {
        const tc = this.template.controls;
        return tc.add || tc.excel || tc.pdf || tc.upload || tc.customToolBarButtons.length > 0;
    }


    /**
     * Excel Export - Exports all columns with applied filters
     * Redifine in template to fo
     */
    allData = (): ExcelExportData => {
        let data = this.template.data;
        if (!this.template.fileName) this.template.fileName = this.template.title;
        if (data.length > 0 && data[0] === undefined) data = [];

        return {
            data: process(data, {
                group: this.template.gridState.group,
                sort: this.template.gridState.sort,
                filter: this.template.gridState.filter
            }).data,
            group: this.template.gridState.group
        };
    }


    /**
     * 
     */
    isHidden = (isVisible: any): boolean => typeof (isVisible) === typeof (true) ? !isVisible : false;


    @HostListener('window:resize')
    onResizeWindow() {
        this.getHeight(true);
    }

    private readonly calculateHeight = () => {
        let height: number;
        let rowHeightTotal = this.gridSize;
        if (rowHeightTotal > this.template.data.length) rowHeightTotal = this.template.data.length;
        if (rowHeightTotal > this.template.gridState.take) rowHeightTotal = this.template.gridState.take;
        if (this.grid.group.length > 0) height = OFFSET_HEIGHT + GROUP_ROW_HEIGHT + rowHeightTotal * ROW_HEIGHT;
        else height = OFFSET_HEIGHT + rowHeightTotal * ROW_HEIGHT;
        if (this.showcaseBody && this.showcaseBody.offsetHeight < height) {
            height = this.showcaseBody.offsetHeight;
        }
        return height;
    }

    /**
     * 
     */
    getHeight = (force = false, state: any = this.template.gridState): number => {
        if (
            this.parentEditDataItem ||
            !this.template.saveUserSettings ||
            !this.grid ||
            !this.template.data || !this.template.data.length) return null;
        this.gridHeight = this.calculateHeight();
        return this.gridHeight;
    }


    /**
     * 
     * @param response 
     */
    private getResponseData(response): any[] {
        return response && response.data && response.total ?
            response.data :
            response && response.length && response[0] ?
                response :
                [];
    }


    /**
     * 
     * @param response 
     */
    private setGridData(response) {
        this.template.data = this.getResponseData(response);
        const _dataResult: GridDataResult = this.template.isServerSideFiltering ?
            process(this.template.data, { group: this.template.gridState.group }) :
            process(this.template.data, this.template.gridState);
        if (response && response.total) _dataResult.total = response.total;
        this.gridData = of(_dataResult);
        this.gridSize = this.template.data.length;
    }



    /**
     * 
     * @param grid 
     * @param rowIndex 
     */
    private closeEditor(sender: any, rowIndex = this.template.editedRowIndex) {
        sender.closeRow(rowIndex);
    }


    /**
     * 
     * @param response 
     * @param messageText 
     */
    private handleResponse(response: Promise<any>, messageText: string) {
        if (!response) return;
        response
            .then(x => {
                this.refresh(true);

                if (this.template.isNotificationsEnabled) {
                    this.cgpSnackbarService.showSnackbar({
                        message: messageText,
                        duration: 2000
                    });
                }
            })
            .catch(e => {
                this.refresh(true);
                const httpErrorHeader = this.template.httpErrorHeader ? this.template.httpErrorHeader : 'Http Error';
                this.handleError(e, `${httpErrorHeader}: ${e.status}`);
            });
    }


    /**
     * 
     * @param message 
     * @param title 
     */
    private handleError(e: any, title = 'Error') {
        this.template.isLoading.next(false);
        const _body = e.error ? e.error : e;
        if (this.cgpAlertDialogService.isOpen()) return;
        this.cgpAlertDialogService.showAlertDialog({
            title,
            message: _body,
            alert: 'danger',
            closeLabel: 'OK',
        });
    }


    // To be overriden by child class
    private readonly exportToExcel = (grid: any) => {
        grid.saveAsExcel();
    }

    /**
     * If serverside pagination is used, override 
     */
    private readonly exportToPDF = (grid: GridComponent) => {
        this.template.exportPDFClick.next(true);
        this.template.read([]).then(response => {
            this.setGridData(response);
            grid.saveAsPDF();
            this.template.exportPDFClick.next(false);
        })

    }

    /**
     * Handler for uploading a file when a file is selected 
     */
    private fileSelectEventHandler(e: any) {
        const template = this.template;
        e.files.forEach((file) => {

            if (!file.validationErrors) {
                const reader = new FileReader();

                reader.onload = (ev) => {
                    const selectedFile = {
                        src: (ev.target as FileReader).result,
                        uid: file.uid,
                        name: file.name,
                        state: file.state,
                        rawFile: file.rawFile,
                    };

                    template.uploadFiles.unshift(selectedFile);
                };

                reader.readAsDataURL(file.rawFile);
            }
        });
    }

    /**
     * Handler for uploading a file when a file is removed 
     */
    private fileRemoveEventHandler(e: any) {
        const index = this.template.uploadFiles.findIndex(item => item.uid === e.files[0].uid);

        if (index >= 0) {
            this.template.uploadFiles.splice(index, 1);
        }
    }

    uploadEventHandler = (e: any, fileInfo: any[]) => {
        this.refresh(false);
    }

    private isFileUploaded() {
        if (this.template.uploadFiles.length > 0 && this.template.uploadFiles[0].state === 3) {//file state = uploaded
            return 3;
        } else if (this.template.uploadFiles.length > 0 && this.template.uploadFiles[0].state === 0) { //file state = failed
            return 0;
        } else {
            return -1;
        }
    }

    /**
     * 
     */
    private readonly configBtn = {
        id: 'configBtn',
        title: 'Customize grid and column properties.',
        text: '',
        class: 'config-btn',
        iconClass: 'fa fa-fw fa-cog',
        callback: this.showConfigDialog,
        bare: true,
    };

    private readonly exportSplitBtn = {
        id: 'exportSplitBtn',
        title: 'export the grid',
        text: 'Export',
        iconClass: 'fa fa-fw fa-download',
        data: [{
            text: ' Excel',
            iconClass: 'fa fa-file-excel-o fa-fw',
            click: this.exportToExcel,
        }, {
            text: 'PDF',
            iconClass: 'fa fa-file-pdf-o fa-fw',
            click: this.exportToPDF,
        },]
    };

    isShowDetailTemplate = (dataItem: any, index: number): boolean => {
        return !!this.template.detailTemplate;
    }

} // End CGP Grid
